import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Col, Form, Image, Row, Stack } from 'react-bootstrap';
import { MdOutlineInfo } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import CustomInputPassword from '../../components/common/CustomInputPassword';
import { default as CustomInputText } from '../../components/common/CustomInputText';
import Loader from '../../components/common/loader';
import { PASSWORD_VALIDATION } from '../../constants/messages';
import sessionStorage from '../../helpers/sessionStorage';
import storage from '../../helpers/storage';
import { getDeviceID } from '../../helpers/utils';
import { loginEmailPasswordAPI } from '../../services/auth.service';
import Auth0LoginButton from './auth0';
import { LoginSchema } from './validation';
import { useCustomStore } from '../../redux/store';
import { redirectionRoutes } from '../../routes/redirectionRoutes';

/**
 * Form values type definition.
 */
type FormValues = {
    email: string;
    password: string;
}

export default function Login() {
    const AppName = process.env.REACT_APP_NAME;
    const { setUser, setAuth0Login } = useCustomStore();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isChecked, setIsChecked] = useState(true);

    const rememberMe: any = null;//storage.getData('customer-remember-me');

    /**
     * Initial Form Values
     */
    const initialValues: FormValues = {
        email: rememberMe ? rememberMe.email : '',
        password: rememberMe ? rememberMe.password : '',
    }

    useEffect(() => {
        if (storage.getToken() === null) {
            storage.clearToken();
            setAuth0Login(false)
        }
    }, [navigate, setAuth0Login])

    /**
     * Handles the change event of a checkbox input.
     * @param {ChangeEvent<HTMLInputElement>} event - The change event object.
     * @returns {void}
     */
    const onChangeCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked)
    };

    /**
     * Submit Method to call when user clicks on login button
     * @async
     * @param {FormValues} values
     * @param {any} actions
     * @returns {*}
     */
    const handleSubmit = async (values: Record<string, any>, actions: any) => {
        setLoading(true);
        actions.setSubmitting(true);
        const formData: any = {
            email: values.email, password: values.password,
            deviceID: values.email ? getDeviceID(values.email) : ''
        };

        loginEmailPasswordAPI(formData).then((response: any) => {
            actions.setSubmitting(false);
            // if (isChecked) {
            //     storage.setData('customer-remember-me', { email: values.email, password: values.password, isChecked });
            // } else {
            //     localStorage.removeItem('customer-remember-me');
            // }
            const { isUsing2FA, rememberDevice, isUsingTOTP, isTOTPSetupRequired, totpData, isChildUser } = response.data;
            if (isUsing2FA) {
                sessionStorage.setData("2fa", values.email);
                navigate(redirectionRoutes.twoFactorAuthentication.path, { state: { email: values.email, auth0Login: false, rememberDevice: rememberDevice, isChildUser: isChildUser }, replace: true });
            } else if (isUsingTOTP) {
                sessionStorage.setData("2fa", values.email);
                if (isTOTPSetupRequired) {
                    navigate(redirectionRoutes.TOTPSetup.path, { state: { email: values.email, auth0Login: false, totpData: totpData, isChildUser: isChildUser }, replace: true });
                } else {
                    navigate(redirectionRoutes.TOTPAuthentication.path, { state: { email: values.email, auth0Login: false, rememberDevice: rememberDevice }, replace: true });
                }
            } else {
                handleLoginResponse(response);
            }
        }).catch((error: any) => {
            console.error(error)
            setLoading(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
            actions.setSubmitting(false);
        })
    }

    /**
     * Handles the response after a successful login.
     * @param {Object} response - The response data from the login API.
     * @returns {void}
     */
    const handleLoginResponse = (response: any) => {
        setLoading(false);
        storage.setToken(response.data.token.access.token);
        storage.setRefreshToken(response.data.token.refresh.token);
        setUser(response.data?.user);
        if(!response.data?.user?.profileVerification?.status) {
            navigate(redirectionRoutes.profile.path, { replace: true });
        } else {
            navigate(redirectionRoutes.dashboard.path, { replace: true });
        }
    }

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100">
                <Col
                    md={6}
                >
                    <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                        <div className='user-select-none'>
                            <Image
                                fluid={true}
                                src={LOGO_WHITE}
                                alt={`${AppName} Logo`}
                                width={240}
                                height={80}
                            />
                        </div>
                        <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName}`}
                                 width={500}
                                 height={350}
                            />
                        </div>
                        <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>AI-Powered</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div>
                    </Stack>
                </Col>
                <Col md={6} className="col-md-6 d-flex flex-column p-3">
                    <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                        <div className='my-auto py-4'>
                            <p className="mb-1 opacity-75 text-uppercase">Welcome to</p>
                            <h1 className="fw-semibold mb-4 pb-1 fs-2">{AppName}</h1>
                            <CommonFormikComponent
                                validationSchema={LoginSchema}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {(formikProps) => (
                                    <>
                                        <CustomInputText
                                            wrapperClassname="mb-3 pb-1"
                                            controlId="email"
                                            label='Email Address'
                                            placeholder="john.smith@example.com"
                                            touched={formikProps.touched.email}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.email}
                                            value={formikProps.values.email}
                                            validationSchema={LoginSchema}
                                        />

                                        <CustomInputPassword
                                            wrapperClassname="mb-3"
                                            controlId="password"
                                            label="Password"
                                            touched={formikProps.touched.password}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.password}
                                            value={formikProps.values.password}
                                            validationSchema={LoginSchema}
                                            tooltip={PASSWORD_VALIDATION}
                                            tooltipContent={<MdOutlineInfo />}
                                        />

                                        <Form.Group className="mb-3 pb-3">
                                            <Row xs="auto" className="align-items-center justify-content-between gx-0">
                                                <Col>
                                                    {/* <CustomCheckbox
                                                        controlId="rememberCheck"
                                                        value="Remember Me"
                                                        label='Remember Me'
                                                        checked={isChecked}
                                                        onChange={onChangeCheckbox}
                                                    /> */}
                                                </Col>
                                                <Col>
                                                    <Link className="fs-14 fw-semibold text-decoration-none" to={redirectionRoutes.forgotPassword.path}>Forgot Password?</Link>
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        <Stack direction="horizontal" gap={3} className="justify-content-end">
                                            <Button className="w-100 rounded-pill" variant="primary" type="submit" disabled={formikProps.isSubmitting || loading}
                                                    style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                                                {loading ? 'Processing...' : 'Login'}
                                            </Button>
                                        </Stack>
                                        {/* <Auth0LoginButton /> */}
                                    </>
                                )}
                            </CommonFormikComponent>
                        </div>
                        <div className='bg-dark bg-opacity-05 custom-min-height-40 fs-13 p-2 rounded text-center w-100 d-inline-flex align-items-center justify-content-center'>
                            <span className='pe-1'>New User?</span> <Link className="fw-semibold text-decoration-none" to={redirectionRoutes.createAccount.path}>Create an Account</Link>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    )
}
